'use client';
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useKrpano } from '@/contexts/KrpanoProvider';
import type { APIHotspot } from '@/types/krpano';
import { useIdPath } from '@/contexts/IdPathProvider';
import { useLanguages } from '@/contexts/LanguageContext';
import multimediaServices from "../variants/default/utils/services/multimedia";
import MultimediaAudioPlayer from '@/components/MultimediaAudioPlayer';
import { buildTitleCss, normalizeHotspotConfig } from '@/templates/Admin/contexts/HotspotManager';
import { createSocketIoT } from '@/utils/socket/socketIoT';
import { localizeIoTConfig, parseIoTAssignmentConfig } from '@/components/iot/IoTMultimedia/types';
import { getHotspotAnchor, getIoTCalloutRows, type IoTRealtimeDeviceData } from '@/components/iot/IoTMultimedia/iot-auto-callout';
import { findKrpanoVisualHotspotName, getKrpanoHotspotTopY } from '@/components/iot/IoTMultimedia/iot-callout-position';
import { buildIoTKrpanoCalloutContent, upsertIoTKrpanoCallout } from '@/components/iot/IoTMultimedia/iot-krpano-callout';

type AdminIoTCalloutSource = {
    hotspot: any;
    config: NonNullable<ReturnType<typeof parseIoTAssignmentConfig>>;
    offsetY: number;
    traitHeight: number;
};

const getAdminIoTCalloutSource = (hotspot: any): AdminIoTCalloutSource | null => {
    try {
        const stored = typeof hotspot?.config === 'string' ? JSON.parse(hotspot.config) : hotspot?.config;
        const callout = stored?.callout;
        const isIoTHotspot = hotspot?.entity_type === 'iot' || hotspot?.link_type === 'Thiết bị IoT';
        if (callout?.enabled === false) return null;
        const config = parseIoTAssignmentConfig(
            callout?.iot || stored?.iot_callout || (isIoTHotspot ? stored?.iot : null),
        );
        if (!callout?.enabled && !isIoTHotspot && !config?.autoShowCallout) return null;
        if (!config?.deviceId || hotspot?.type === 'polygon') return null;
        return {
            hotspot,
            config,
            offsetY: Number(callout.offsetY ?? callout.oy ?? 0) || 0,
            traitHeight: Number(callout.height ?? 70) || 70,
        };
    } catch {
        return null;
    }
};

const getAdminIoTCalloutTraitName = (id: number | string) => `admin_iot_callout_${id}_trait`;
const getAdminIoTCalloutPanelName = (id: number | string) => `admin_iot_callout_${id}_panel`;

// Helper chuyển đổi màu
const hexToRgba = (hex: string, alpha: number) => {
    if (!hex) return 'rgba(255, 255, 255, 1)';

    const cleanHex = hex.replace('0x', '#').replace('#', '');

    let r = 0, g = 0, b = 0;
    if (cleanHex.length === 3) {
        r = parseInt(cleanHex[0] + cleanHex[0], 16);
        g = parseInt(cleanHex[1] + cleanHex[1], 16);
        b = parseInt(cleanHex[2] + cleanHex[2], 16);
    } else if (cleanHex.length >= 6) {
        r = parseInt(cleanHex.substring(0, 2), 16);
        g = parseInt(cleanHex.substring(2, 4), 16);
        b = parseInt(cleanHex.substring(4, 6), 16);
    }
    return `rgba(${r},${g},${b},${alpha})`;
};

// Helper chuyển đổi màu viền
const toCssColor = (hex: string) => {
    if (!hex) return '#000000';
    return hex.replace('0x', '#');
};

const normalizeLineBreakText = (value: string = '') => {
    return value
        .replace(/<br\s*\/?\s*>/gi, '\n')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');
};

const toSafeHtmlWithLineBreaks = (value: string = '') => {
    const normalized = normalizeLineBreakText(value);
    return normalized
        .replace(/&/g, '&amp;')
        .replace(/'/g, '&#39;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');
};

const toKrpanoTooltipText = (value: string = '') => {
    return normalizeLineBreakText(value).replace(/\n/g, '[br]');
};

// YouTube URL helpers
const isYoutubeUrl = (url: string | undefined | null): boolean => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
};

const getYoutubeEmbedUrl = (url: string, muted: boolean = false): string => {
    if (!url) return '';

    // If already an embed URL, update the mute parameter
    if (url.includes('youtube.com/embed/')) {
        return url.replace(/mute=\d/, `mute=${muted ? 1 : 0}`);
    }

    let videoId = '';
    const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);
    if (watchMatch) videoId = watchMatch[1];
    else {
        const shortMatch = url.match(/youtu\.be\/([^?]+)/);
        if (shortMatch) videoId = shortMatch[1];
    }

    if (!videoId) return url;
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${muted ? 1 : 0}&controls=1&rel=0`;
};

// Hàm tính toán vị trí text dựa trên text_position (tham khảo từ trang sản phẩm)
function calculateTextPosition(
    coordinates: Array<{ ath: number; atv: number }>,
    position: string = 'inside-center'
) {
    // Tính tâm polygon
    const centerAth = coordinates.reduce((sum, point) => sum + point.ath, 0) / coordinates.length;
    const centerAtv = coordinates.reduce((sum, point) => sum + point.atv, 0) / coordinates.length;

    // Tìm các điểm biên
    const minAth = Math.min(...coordinates.map(p => p.ath));
    const maxAth = Math.max(...coordinates.map(p => p.ath));
    const minAtv = Math.min(...coordinates.map(p => p.atv));
    const maxAtv = Math.max(...coordinates.map(p => p.atv));

    // Tính khoảng cách để offset hợp lý
    const margin = 5; // Khoảng cách từ biên

    let ath = centerAth;
    let atv = centerAtv;
    let edge = 'center';
    let ox = 0;
    let oy = 0;

    switch (position) {
        // BÊN TRONG
        case 'inside-center':
            ath = centerAth;
            atv = centerAtv;
            edge = 'center';
            ox = 0;
            oy = 0;
            break;

        case 'inside-top-left':
            ath = minAth + (maxAth - minAth) * 0.1; // 10% từ cạnh trái
            atv = minAtv + (maxAtv - minAtv) * 0.1; // 10% từ cạnh trên
            edge = 'topleft';
            ox = margin;
            oy = margin;
            break;

        // BÊN NGOÀI
        case 'outside-top-left':
            // Lấy góc trên-trái của polygon, text nằm bên phải và phía trên
            ath = minAth;
            atv = minAtv;
            edge = 'bottomleft'; // Neo góc dưới trái của text vào điểm góc polygon
            ox = margin; // Offset sang phải
            oy = -margin; // Offset lên trên
            break;

        default:
            // Mặc định: bên trong - chính giữa
            ath = centerAth;
            atv = centerAtv;
            edge = 'center';
            ox = 0;
            oy = 0;
            break;
    }

    return { ath, atv, edge, ox, oy };
}

interface MultimediaManagerContextType {
    hotspots: APIHotspot[];
    currentSceneHotspots: APIHotspot[];
    hotspotDataList: any[];
    isLoading: boolean;
    fetchMultimedia: (sceneName: string, forceReload?: boolean) => Promise<void>;
    // Popup state
    popupHotspot: APIHotspot | null;
    popupPosition: { x: number; y: number } | null;
    isPopupVisible: boolean;
    showPopup: (hotspot: APIHotspot, position: { x: number; y: number }) => void;
    hidePopup: () => void;
    // Update form state
    updateFormHotspot: APIHotspot | null;
    isUpdateFormOpen: boolean;
    // Audio player state
    audioPlayerData: any | null;
    isAudioPlayerVisible: boolean;
    playAudio: (audioData: any) => void;
    stopAudio: () => void;
    openUpdateForm: (hotspot: APIHotspot) => void;
    closeUpdateForm: () => void;
    // Edit and Delete functions
    updateMultimedia: (id: number, data: any) => Promise<void>;
    deleteMultimedia: (id: number) => Promise<void>;
    reloadSingleHotspot: (hotspotId: number | string) => void;
    isCreateFormOpen: boolean;
    setIsCreateFormOpen: (isOpen: boolean) => void;
}

const MultimediaManagerContext = createContext<MultimediaManagerContextType | undefined>(undefined);

export const MultimediaManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const { krpano, krpanoCurrent, currentScene, isReadyKrpano } = useKrpano();
    const { idPath } = useIdPath();
    const { currentLanguage } = useLanguages();

    const [hotspots, setHotspots] = useState<APIHotspot[]>([]);
    const [currentSceneHotspots, setCurrentSceneHotspots] = useState<APIHotspot[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lastScene, setLastScene] = useState<string>('');
    const [hotspotDataList, setHotspotDataList] = useState<any[]>([]);
    const [iotCalloutDataByDeviceId, setIoTCalloutDataByDeviceId] = useState<Record<string, IoTRealtimeDeviceData>>({});
    const adminIoTCalloutIdsRef = useRef<Set<string>>(new Set());

    // Popup state
    const [popupHotspot, setPopupHotspot] = useState<APIHotspot | null>(null);
    const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);
    const [isPopupVisible, setIsPopupVisible] = useState(false);

    // Update form state
    const [updateFormHotspot, setUpdateFormHotspot] = useState<APIHotspot | null>(null);
    const [isUpdateFormOpen, setIsUpdateFormOpen] = useState(false);

    // Audio player state
    const [audioPlayerData, setAudioPlayerData] = useState<any | null>(null);
    const [isAudioPlayerVisible, setIsAudioPlayerVisible] = useState(false);

    // Create form state
    const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
    const adminIoTCalloutSources = useMemo(
        () => hotspotDataList.map(getAdminIoTCalloutSource).filter((source): source is AdminIoTCalloutSource => Boolean(source)),
        [hotspotDataList],
    );
    const adminIoTDeviceKey = useMemo(
        () => Array.from(new Set(adminIoTCalloutSources.map((source) => String(source.config.deviceId)))).sort().join(','),
        [adminIoTCalloutSources],
    );

    // Helper functions
    // Robust scale setter to avoid race conditions when loadstyle or plugin loading resets the scale.
    const setHotspotScale = useCallback((hotspotName: string, value?: number) => {
        if (!krpano || value === undefined || Number.isNaN(value)) return;
        try {
            krpano.call(`delayedcall(0.02, set(hotspot[${hotspotName}].scale, ${value}); tween(hotspot[${hotspotName}].scale, ${value}, 0.0, default, 0.1)); delayedcall(0.6, set(hotspot[${hotspotName}].scale, ${value}));`);
        } catch (err) {
            try { krpano.set(`hotspot[${hotspotName}].scale`, value); } catch (e) { /* no-op */ }
            setTimeout(() => { try { krpano.set(`hotspot[${hotspotName}].scale`, value); } catch (e) { /* no-op */ } }, 700);
        }
        try {
            const existingOnloaded = krpano.get(`hotspot[${hotspotName}].onloaded`);
            const appendCmd = `set(hotspot[${hotspotName}].scale, ${value});`;
            // Append instead of replacing to avoid clobbering existing onloaded behaviors
            krpano.set(`hotspot[${hotspotName}].onloaded`, `${existingOnloaded || ''}${existingOnloaded ? '; ' : ''}${appendCmd}`);
        } catch (e) { /* no-op */ }
    }, [krpano]);
    const showPopup = (hotspot: APIHotspot, position: { x: number; y: number }) => {
        setPopupHotspot(hotspot);
        setPopupPosition(position);
        setIsPopupVisible(true);
    };

    const hidePopup = () => {
        setIsPopupVisible(false);
        setPopupHotspot(null);
        setPopupPosition(null);
    };

    const openUpdateForm = (hotspot: APIHotspot) => {
        setUpdateFormHotspot(hotspot);
        setIsUpdateFormOpen(true);
    };

    const closeUpdateForm = () => {
        setIsUpdateFormOpen(false);
        setUpdateFormHotspot(null);
    };

    // Audio player functions
    const stopAudio = useCallback(() => {
        setIsAudioPlayerVisible(false);
        setAudioPlayerData(null);
    }, []);

    const playAudio = useCallback((audioData: any) => {
        // Stop any currently playing audio first
        stopAudio();

        // Pause all other audio sources (voice scene, voice group, background music)
        if (typeof window !== 'undefined') {
            const win = window as Window & {
                stopVoiceScene?: () => void;
                stopVoiceGroup?: () => void;
                pauseBackgroundMusic?: () => void;
            };
            // Stop voice scene
            if (win.stopVoiceScene) {
                win.stopVoiceScene();
            }
            // Stop voice group
            if (win.stopVoiceGroup) {
                win.stopVoiceGroup();
            }
            // Stop background music
            if (win.pauseBackgroundMusic) {
                win.pauseBackgroundMusic();
            }
        }

        // Set audio player data and show player
        setAudioPlayerData(audioData);
        setIsAudioPlayerVisible(true);
    }, [stopAudio]);

    const renderTextbox = useCallback((hotspotData: any) => {
        const hotspotName = `hotspot_${hotspotData.id}`; // Dùng chung prefix để dễ quản lý xóa

        if (!krpano) return;
        if (krpano.get(`hotspot[${hotspotName}]`)) return;

        try {
            // 1. Parse Config JSON (Xử lý logic Double Stringify)
            // DB lưu: config = "{\"Title\":\"{\\\"color\\\":...}\", ...}"
            let configObj: any = {};
            let cssStyleObj: any = {};

            if (hotspotData.config) {
                try {
                    configObj = typeof hotspotData.config === 'string'
                        ? JSON.parse(hotspotData.config)
                        : hotspotData.config;

                    if (configObj.Title) {
                        cssStyleObj = typeof configObj.Title === 'string'
                            ? JSON.parse(configObj.Title)
                            : configObj.Title;
                    }
                } catch (e) {
                    console.warn("Lỗi parse config textbox:", e);
                }
            }

            // 2. Lấy nội dung văn bản
            // Ưu tiên lấy từ translations (đa ngôn ngữ), nếu không thì lấy title gốc
            let contentText = hotspotData.title || "";
            if (hotspotData.translations && Array.isArray(hotspotData.translations)) {
                const translation = hotspotData.translations.find((t: any) => t.language_id === 'vn') || hotspotData.translations[0];
                if (translation) contentText = translation.title || translation.information || contentText;
            }

            // Chuyển đổi xuống dòng \n thành [br] cho Krpano
            const htmlContent = (contentText || "").replace(/\n/g, '[br]');

            // 3. Tái tạo CSS Style
            const styleString = `
                font-family: ${cssStyleObj['font-family'] || 'Arial'};
                font-size: ${cssStyleObj['font-size'] || '14px'};
                color: ${toCssColor(cssStyleObj['color'])};
                font-weight: ${cssStyleObj['font-weight'] || 'normal'};
                font-style: ${cssStyleObj['font-style'] || 'normal'};
                text-decoration: ${cssStyleObj['text-decoration'] || 'none'};
                text-align: ${cssStyleObj['text-align'] || 'left'};
                
                display: flex;
                flex-direction: column;
                justify-content: ${cssStyleObj['justify-content'] || 'flex-start'};
                
                width: 100%;
                height: 100%;
                box-sizing: border-box;
                padding: ${cssStyleObj['padding'] || '10px'};
                
                /* Màu nền & Viền & Bo góc */
                background-color: ${hexToRgba(hotspotData.color_fill, hotspotData.color_fill_alpha ?? 1)};
                border: ${cssStyleObj['border'] ? cssStyleObj['border'] : `${hotspotData.border_width}px solid ${toCssColor(hotspotData.color_line)}`};
                border-radius: ${cssStyleObj['border-radius'] || '0px'};
                
                word-wrap: break-word;
                white-space: normal;
                pointer-events: auto;
            `.replace(/\n/g, '');

            // 4. Lấy Tọa độ VÀ GÓC XOAY
            const coordinate = hotspotData.coordinate && hotspotData.coordinate[0]
                ? hotspotData.coordinate[0]
                : { ath: 0, atv: 0, rx: 0, ry: 0, rz: 0 };

            // 5. Gọi lệnh Krpano
            krpano.call(`
                addhotspot(${hotspotName});
                set(hotspot[${hotspotName}].type, text);
                
                set(hotspot[${hotspotName}].ath, ${coordinate.ath});
                set(hotspot[${hotspotName}].atv, ${coordinate.atv});
                
                set(hotspot[${hotspotName}].rx, ${coordinate.rx || 0});
                set(hotspot[${hotspotName}].ry, ${coordinate.ry || 0});
                set(hotspot[${hotspotName}].rz, ${coordinate.rz || 0});
                set(hotspot[${hotspotName}].rotationorder, 'yxz');
                
                set(hotspot[${hotspotName}].width, ${parseInt(cssStyleObj.width) || 300});
                set(hotspot[${hotspotName}].height, ${parseInt(cssStyleObj.height) || 150});
                
                // set(hotspot[${hotspotName}].renderer, css3d);
                set(hotspot[${hotspotName}].distorted, true);
                set(hotspot[${hotspotName}].zoom, true);
                set(hotspot[${hotspotName}].visible, true);
                set(hotspot[${hotspotName}].enabled, true);
                set(hotspot[${hotspotName}].handcursor, false);
                set(hotspot[${hotspotName}].capture, false);
                set(hotspot[${hotspotName}].zorder, 10); // Higher than Image2D(1), but lower than icons(100)
                
                set(hotspot[${hotspotName}].bg, true);
                set(hotspot[${hotspotName}].bgalpha, 0);
                set(hotspot[${hotspotName}].bgcolor, 0x000000);
                set(hotspot[${hotspotName}].bgborder, "0 0x000000 0");
                
                set(hotspot[${hotspotName}].padding, 0);
                set(hotspot[${hotspotName}].shadow, "");

                set(hotspot[${hotspotName}].onclick, js(window.showMultimediaPopup('${hotspotData.id}', get(mouse.stagex), get(mouse.stagey))));
            `);
            krpano.set(`hotspot[${hotspotName}].html`, htmlContent);
            krpano.set(`hotspot[${hotspotName}].css`, styleString);

            // Apply scale if defined on textbox
            try {
                const hasScale = hotspotData?.scale !== undefined && hotspotData?.scale !== null;
                const hasMobileScale = hotspotData?.scale_mobile !== undefined && hotspotData?.scale_mobile !== null;
                let effectiveScale: number | undefined = undefined;
                if (typeof window !== 'undefined' && window.innerWidth <= 768 && hasMobileScale) {
                    effectiveScale = Number(hotspotData.scale_mobile);
                } else if (hasScale) {
                    effectiveScale = Number(hotspotData.scale);
                }
                if (effectiveScale !== undefined && !Number.isNaN(effectiveScale)) {
                    setHotspotScale(hotspotName, effectiveScale);
                }
            } catch (e) {
                console.warn('[MultimediaManager] Could not apply textbox scale:', e);
            }

        } catch (error) {
            console.error("Error rendering textbox:", error, hotspotData);
        }
    }, [krpano]);

    // Render Image2D from database
    const renderImage2D = useCallback((hotspotData: any) => {
        const hotspotName = `hotspot_${hotspotData.id}`;

        if (!krpano) return;
        if (krpano.get(`hotspot[${hotspotName}]`)) return;

        try {
            // Parse config JSON
            let configObj: any = {};
            if (hotspotData.config) {
                try {
                    configObj = typeof hotspotData.config === 'string'
                        ? JSON.parse(hotspotData.config)
                        : hotspotData.config;
                } catch (e) {
                    console.warn("Error parsing image2d config:", e);
                }
            }

            // Get image URL from config (multi-language support)
            let imageUrl = '';
            const langCode = currentLanguage?.id || 'vi';

            if (Array.isArray(configObj.images)) {
                // Find matching language
                const matchingImg = configObj.images.find((img: any) =>
                    img.language === langCode ||
                    (langCode === 'vi' && img.language === 'vn') ||
                    (langCode === 'vn' && img.language === 'vi')
                );
                if (matchingImg) {
                    imageUrl = matchingImg.url;
                } else if (configObj.images.length > 0) {
                    // Fallback to first image if no match
                    imageUrl = configObj.images[0].url;
                }
            } else if (configObj.imageUrls && typeof configObj.imageUrls === 'object') {
                imageUrl = configObj.imageUrls[langCode] || configObj.imageUrls['vi'] || configObj.imageUrls['vn'] || Object.values(configObj.imageUrls)[0] as string;
            }

            // Legacy fallback
            if (!imageUrl) {
                imageUrl = configObj.imageUrl || '';
            }

            if (!imageUrl) {
                console.warn("No imageUrl found for image2d:", hotspotData.id);
                return;
            }

            // Get scale - priority: hotspotData.scale (separate column) > config.scale > 1
            const scale = hotspotData.scale ?? configObj.scale ?? 1;

            // Get width and height from config (important for maintaining aspect ratio)
            const width = configObj.width;
            const height = configObj.height;

            // Get coordinates and rotation
            const coordinate = hotspotData.coordinate && hotspotData.coordinate[0]
                ? hotspotData.coordinate[0]
                : { ath: 0, atv: 0, rx: 0, ry: 0, rz: 0 };

            // Build width/height commands if they exist in config
            const widthCmd = width ? `set(hotspot[${hotspotName}].width, ${width});` : '';
            const heightCmd = height ? `set(hotspot[${hotspotName}].height, ${height});` : '';

            // Create hotspot with scale and dimensions applied in onloaded to ensure it takes effect after image loads
            krpano.call(`
                addhotspot(${hotspotName});
                set(hotspot[${hotspotName}].type, image);
                set(hotspot[${hotspotName}].url, "${imageUrl}");
                set(hotspot[${hotspotName}].ath, ${coordinate.ath});
                set(hotspot[${hotspotName}].atv, ${coordinate.atv});
                set(hotspot[${hotspotName}].rx, ${coordinate.rx || 0});
                set(hotspot[${hotspotName}].ry, ${coordinate.ry || 0});
                set(hotspot[${hotspotName}].rz, ${coordinate.rz || 0});
                set(hotspot[${hotspotName}].rotationorder, 'yxz');
                set(hotspot[${hotspotName}].scale, ${scale});
                ${widthCmd}
                ${heightCmd}
                // set(hotspot[${hotspotName}].renderer, css3d);
                set(hotspot[${hotspotName}].distorted, true);
                set(hotspot[${hotspotName}].zoom, true);
                set(hotspot[${hotspotName}].visible, true);
                set(hotspot[${hotspotName}].enabled, true);
                set(hotspot[${hotspotName}].handcursor, true);
                set(hotspot[${hotspotName}].zorder, 1);
                set(hotspot[${hotspotName}].onloaded, set(hotspot[${hotspotName}].scale, ${scale}); ${widthCmd} ${heightCmd});
                set(hotspot[${hotspotName}].onclick, js(window.showMultimediaPopup('${hotspotData.id}', get(mouse.stagex), get(mouse.stagey))));
            `);

            // Also set scale directly after a short delay to ensure it's applied
            setTimeout(() => {
                try {
                    krpano.set(`hotspot[${hotspotName}].scale`, scale);
                } catch (e) {
                    // Ignore
                }
            }, 200);

        } catch (error) {
            console.error("Error rendering image2d:", error, hotspotData);
        }
    }, [krpano]);

    // Render Video2D from database
    const renderVideo2D = useCallback((hotspotData: any) => {
        const hotspotName = `hotspot_${hotspotData.id}`;

        if (!krpano) return;
        if (krpano.get(`hotspot[${hotspotName}]`)) return;

        // DEBUG: Log exact data to compare with Heritage
        console.log('[Admin Video2D] Raw hotspotData:', JSON.stringify({
            id: hotspotData.id,
            coordinate: hotspotData.coordinate,
            scale: hotspotData.scale,
            config: hotspotData.config,
            type: hotspotData.type,
            entity_type: hotspotData.entity_type,
        }, null, 2));

        try {
            // Parse config JSON
            let configObj: any = {};
            if (hotspotData.config) {
                try {
                    configObj = typeof hotspotData.config === 'string'
                        ? JSON.parse(hotspotData.config)
                        : hotspotData.config;
                } catch (e) {
                    console.warn("Error parsing video2d config:", e);
                }
            }

            // Get video URL from config (multi-language support)
            let videoUrl = '';
            const langCode = currentLanguage?.id || 'vi';

            if (Array.isArray(configObj.videos)) {
                const matchingVid = configObj.videos.find((vid: any) =>
                    vid.language === langCode ||
                    (langCode === 'vi' && vid.language === 'vn') ||
                    (langCode === 'vn' && vid.language === 'vi')
                );
                if (matchingVid) {
                    videoUrl = matchingVid.url;
                } else if (configObj.videos.length > 0) {
                    videoUrl = configObj.videos[0].url;
                }
            } else if (configObj.videoUrls && typeof configObj.videoUrls === 'object') {
                videoUrl = configObj.videoUrls[langCode] || configObj.videoUrls['vi'] || configObj.videoUrls['vn'] || Object.values(configObj.videoUrls)[0] as string;
            }

            // Legacy fallback
            if (!videoUrl) {
                videoUrl = configObj.videoUrl || '';
            }

            if (!videoUrl) {
                console.warn("No videoUrl found for video2d:", hotspotData.id);
                return;
            }

            const isYoutube = isYoutubeUrl(videoUrl);
            const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 768;

            // Video2D uses one unified scale across desktop/mobile.
            const scale = hotspotData.scale ?? configObj.scale ?? 1;

            // Get width and height
            const width = configObj.width || 400;
            const height = isYoutube ? Math.round(width * 9 / 16) : (configObj.height || 300);
            const volume = configObj.volume ?? 0.5;
            const autoplay = configObj.autoplay !== false;
            const loop = configObj.loop !== false;
            const muted = configObj.muted || false;

            // Adapt Video2D size to the current scene framing on mobile.
            const responsiveReference =
                configObj.responsiveReference && typeof configObj.responsiveReference === 'object'
                    ? configObj.responsiveReference
                    : {};

            let responsiveFactor = 1;
            if (isMobileViewport) {
                const currentVfov = Number(krpano.get('view.vfov') || krpano.get('view.fov'));
                const baseVfov = Number(responsiveReference.vfov);
                const currentStageWidth = Number(krpano.get('stagewidth'));
                const currentStageHeight = Number(krpano.get('stageheight'));
                const baseStageWidth = Number(responsiveReference.stageWidth);
                const baseStageHeight = Number(responsiveReference.stageHeight);

                if (Number.isFinite(currentVfov) && Number.isFinite(baseVfov) && baseVfov > 0) {
                    const fovRatio = Math.tan((currentVfov * Math.PI) / 360) / Math.tan((baseVfov * Math.PI) / 360);
                    let adjustedRatio = fovRatio;

                    if (
                        Number.isFinite(currentStageWidth) &&
                        Number.isFinite(currentStageHeight) &&
                        Number.isFinite(baseStageWidth) &&
                        Number.isFinite(baseStageHeight) &&
                        currentStageWidth > 0 &&
                        currentStageHeight > 0 &&
                        baseStageWidth > 0 &&
                        baseStageHeight > 0
                    ) {
                        const currentAspect = currentStageWidth / currentStageHeight;
                        const baseAspect = baseStageWidth / baseStageHeight;

                        if (Number.isFinite(currentAspect) && Number.isFinite(baseAspect) && currentAspect > 0 && baseAspect > 0) {
                            // Apply a mild aspect compensation to avoid slight oversizing on narrow mobile screens.
                            const aspectCompensation = Math.pow(currentAspect / baseAspect, 0.25);
                            adjustedRatio = fovRatio * aspectCompensation;
                        }
                    }

                    responsiveFactor = Math.min(1.6, Math.max(0.5, adjustedRatio));
                } else if (
                    Number.isFinite(currentStageWidth) &&
                    Number.isFinite(currentStageHeight) &&
                    Number.isFinite(baseStageWidth) &&
                    Number.isFinite(baseStageHeight) &&
                    baseStageWidth > 0 &&
                    baseStageHeight > 0
                ) {
                    const stageRatio = Math.min(
                        currentStageWidth / baseStageWidth,
                        currentStageHeight / baseStageHeight
                    );
                    responsiveFactor = Math.min(1.6, Math.max(0.5, stageRatio));
                }
            }

            const displayWidth = Math.max(1, Math.round(width * responsiveFactor));
            const displayHeight = Math.max(1, Math.round(height * responsiveFactor));

            // Get coordinates and rotation
            const coordinate = hotspotData.coordinate && hotspotData.coordinate[0]
                ? hotspotData.coordinate[0]
                : { ath: 0, atv: 0, rx: 0, ry: 0, rz: 0 };

            // Build video HTML (use displayWidth/displayHeight for mobile-friendly sizing)
            const embedUrl = isYoutube ? getYoutubeEmbedUrl(videoUrl, muted) : videoUrl;


            const videoHtml = isYoutube
                ? `<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:${displayWidth}px;height:${displayHeight}px;overflow:hidden;">
                     <iframe src="${embedUrl}" style="width:100%;height:100%;pointer-events:none;border:none;" frameborder="0" allow="autoplay; encrypted-media; fullscreen" allowfullscreen></iframe>
                   </div>`
                : `<video src="${videoUrl}" style="width:${displayWidth}px;height:${displayHeight}px;object-fit:contain;" ${autoplay ? 'autoplay' : ''} ${loop ? 'loop' : ''} ${muted ? 'muted' : ''} playsinline></video>`;

            // Create CSS3D text hotspot with embedded video
            krpano.call(`
                addhotspot(${hotspotName});
                set(hotspot[${hotspotName}].type, text);
                set(hotspot[${hotspotName}].renderer, css3d);
                set(hotspot[${hotspotName}].edge, center);
                set(hotspot[${hotspotName}].ath, ${coordinate.ath});
                set(hotspot[${hotspotName}].atv, ${coordinate.atv});
                set(hotspot[${hotspotName}].rx, ${coordinate.rx || 0});
                set(hotspot[${hotspotName}].ry, ${coordinate.ry || 0});
                set(hotspot[${hotspotName}].rz, ${coordinate.rz || 0});
                set(hotspot[${hotspotName}].rotationorder, 'yxz');
                set(hotspot[${hotspotName}].scale, ${scale});
                set(hotspot[${hotspotName}].width, ${displayWidth});
                set(hotspot[${hotspotName}].height, ${displayHeight});
                set(hotspot[${hotspotName}].distorted, true);
                set(hotspot[${hotspotName}].zoom, true);
                set(hotspot[${hotspotName}].visible, true);
                set(hotspot[${hotspotName}].enabled, true);
                set(hotspot[${hotspotName}].handcursor, true);
                set(hotspot[${hotspotName}].capture, false);
                set(hotspot[${hotspotName}].zorder, 1);
                set(hotspot[${hotspotName}].bgcolor, 0x000000);
                set(hotspot[${hotspotName}].bgalpha, 0.0);
                set(hotspot[${hotspotName}].onloaded, set(hotspot[${hotspotName}].scale, ${scale}));
                set(hotspot[${hotspotName}].onclick, js(window.showMultimediaPopup('${hotspotData.id}', get(mouse.stagex), get(mouse.stagey))));
            `);

            // Set HTML content with video
            try {
                krpano.set(`hotspot[${hotspotName}].html`, videoHtml);
            } catch (e) {
                console.warn("Could not set video HTML:", e);
            }

            // Set volume on the video element after a short delay
            setTimeout(() => {
                try {
                    krpano.set(`hotspot[${hotspotName}].scale`, scale);
                    // Try to set volume on the actual video element
                    const hotspotEl = document.querySelector(`[data-hotspot="${hotspotName}"] video`) as HTMLVideoElement;
                    if (hotspotEl) {
                        hotspotEl.volume = volume;
                    }
                } catch (e) {
                    // Ignore
                }
            }, 500);

        } catch (error) {
            console.error("Error rendering video2d:", error, hotspotData);
        }
    }, [krpano]);

    // Helper function to create text label hotspot - uses buildTitleCss for consistent styling with Product page
    const createTextLabelHotspot = useCallback((
        krpanoInstance: any,
        textHotspotName: string,
        hotspot: any,
        coordinate: { ath: number; atv: number; edge?: string; ox?: number; oy?: number }
    ) => {
        if (!krpanoInstance || !hotspot?.title) return;

        // IMPORTANT: Always remove existing text hotspot first to prevent duplicates
        try {
            krpanoInstance.call(`removehotspot(${textHotspotName})`);
        } catch {
            // Ignore errors if text hotspot doesn't exist
        }

        // Add the text hotspot
        krpanoInstance.call(`addhotspot(${textHotspotName})`);

        // Build CSS using buildTitleCss (same as Product page) for consistent styling
        const config = normalizeHotspotConfig(hotspot.config);

        // Calculate actual text width to determine if we should use 'auto' or 'fixed' width
        // (Similar logic to Navigation hotspots in HotspotManager)

        const titleConfig = (config?.Title && typeof config.Title === 'object') ? config.Title : {};
        const maxWidthPx = parseInt(titleConfig['max-width'] || '200'); // Default to 200 consistent with Navigation
        const fontSize = titleConfig['font-size'] || '14px';
        const fontFamily = titleConfig['font-family'] || 'Arial';

        let textWidth = 0;
        try {
            if (typeof document !== 'undefined') {
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                if (context) {
                    context.font = `${fontSize} ${fontFamily}`;
                    textWidth = context.measureText(hotspot.title || '').width;
                }
            }
        } catch (e) {
            console.warn('[MultimediaManager] Failed to measure text width:', e);
        }

        // Determine frameWidth and wrapping behavior based on measured text width
        // Logic: 
        // 1. If width is explicitly set in config, use it.
        // 2. If text is short (< max-width), use 'auto' width and DISBALE wordwrap to ensure expansion.
        // 3. If text is long (>= max-width), use fixed 'max-width' and ENABLE wordwrap.
        let frameWidth: string | number = 'auto';
        let shouldWordWrap = true;
        let whiteSpace = 'normal';

        if (titleConfig.width && titleConfig.width !== 'auto') {
            frameWidth = titleConfig.width;
            shouldWordWrap = true;
            whiteSpace = titleConfig['white-space'] || 'normal';
        } else if (textWidth > 0 && textWidth <= maxWidthPx) {
            frameWidth = 'auto';
            shouldWordWrap = false;
            whiteSpace = 'nowrap'; // Prevent wrapping for short text
        } else {
            frameWidth = maxWidthPx;
            shouldWordWrap = true;
            whiteSpace = 'normal';
        }

        const titleCss = buildTitleCss(hotspot, config).replace(/\n/g, '').replace(/"/g, "'");
        // Inject/Override white-space and width in CSS to match our calculated behavior
        const finalTitleCss = titleCss
            .replace(/white-space:[^;]+;/, `white-space:${whiteSpace};`)
            .replace(/width:[^;]+;/, `width:${frameWidth === 'auto' ? 'auto' : frameWidth + 'px'};`);

        // Escape text for HTML
        const escapedText = toSafeHtmlWithLineBreaks(hotspot.title || '');

        // Use hotspot type="text" instead of layer (layer doesn't support ath/atv)
        krpanoInstance.set(`hotspot[${textHotspotName}].type`, 'text');
        krpanoInstance.set(`hotspot[${textHotspotName}].html`, `<div style="${finalTitleCss}">${escapedText}</div>`);
        krpanoInstance.set(`hotspot[${textHotspotName}].ath`, coordinate.ath);
        krpanoInstance.set(`hotspot[${textHotspotName}].atv`, coordinate.atv);

        // Use edge, ox, oy from coordinate if provided (for polygon), otherwise use defaults (for 2D hotspot)
        const edge = coordinate.edge !== undefined ? coordinate.edge : 'top';
        const ox = coordinate.ox !== undefined ? coordinate.ox : 0;
        const oy = coordinate.oy !== undefined ? coordinate.oy : '10'; // Offset below the icon for 2D

        krpanoInstance.set(`hotspot[${textHotspotName}].edge`, edge);
        krpanoInstance.set(`hotspot[${textHotspotName}].ox`, ox);
        krpanoInstance.set(`hotspot[${textHotspotName}].oy`, oy);

        // Parse width value to extract numeric value for Krpano (e.g., "350px" -> 350)
        let krpanoWidth = frameWidth;
        if (typeof krpanoWidth === 'string' && krpanoWidth !== 'auto') {
            const numericMatch = krpanoWidth.match(/^(\d+(?:\.\d+)?)/);
            if (numericMatch) {
                krpanoWidth = parseFloat(numericMatch[1]);
            }
        }


        // Set width from config and enable word wrapping (matching Product page)
        krpanoInstance.set(`hotspot[${textHotspotName}].width`, krpanoWidth);
        krpanoInstance.set(`hotspot[${textHotspotName}].wordwrap`, shouldWordWrap);
        krpanoInstance.set(`hotspot[${textHotspotName}].vcenter`, true);

        // Disable Krpano native styling to rely on CSS (matching Product page)
        krpanoInstance.set(`hotspot[${textHotspotName}].bg`, true);
        krpanoInstance.set(`hotspot[${textHotspotName}].bgalpha`, 0);
        krpanoInstance.set(`hotspot[${textHotspotName}].bgborder`, "0");
        krpanoInstance.set(`hotspot[${textHotspotName}].css`, "");

        // IMPORTANT: Use renderer: webgl (default) to be in same layer as Polygons
        // krpanoInstance.set(`hotspot[${textHotspotName}].renderer`, 'css3d');
        krpanoInstance.set(`hotspot[${textHotspotName}].distorted`, true);
        krpanoInstance.set(`hotspot[${textHotspotName}].visible`, true);
        krpanoInstance.set(`hotspot[${textHotspotName}].enabled`, false); // Don't capture clicks
        krpanoInstance.set(`hotspot[${textHotspotName}].zorder`, 101); // Higher than icon (100) and Image2D (1)
    }, []);

    // Ensure hotspot icon loads reliably - automatic retry if image fails to load from S3
    // Fixes: icon sometimes not showing on slow networks, only title visible
    const ensureHotspotIconLoaded = useCallback((hotspotName: string) => {
        if (!krpano) return;

        const MAX_RETRIES = 5;
        const RETRY_DELAY = 2500;
        let retryCount = 0;

        // Pre-fetch icon URL via JS Image to warm browser cache
        try {
            const iconUrl = String(krpano.get(`hotspot[${hotspotName}].url`) || '');
            if (iconUrl && iconUrl !== 'textfield.swf' && !iconUrl.startsWith('data:')) {
                const prefetch = new Image();
                prefetch.crossOrigin = 'anonymous';
                prefetch.src = iconUrl;
            }
        } catch (e) { /* ignore */ }

        const retryLoad = () => {
            if (retryCount >= MAX_RETRIES || !krpano) return;
            try {
                if (!krpano.get(`hotspot[${hotspotName}]`)) return;
                const isLoaded = krpano.get(`hotspot[${hotspotName}].loaded`);
                if (isLoaded) return;

                const isVisible = krpano.get(`hotspot[${hotspotName}].visible`);
                if (isVisible === false || isVisible === 'false') return;

                retryCount++;

                const currentUrl = String(krpano.get(`hotspot[${hotspotName}].url`) || '');
                if (currentUrl && currentUrl !== 'textfield.swf' && !currentUrl.startsWith('data:')) {
                    const cleanUrl = currentUrl.replace(/[?&]_rt=\d+/g, '');
                    const sep = cleanUrl.includes('?') ? '&' : '?';
                    const retryUrl = `${cleanUrl}${sep}_rt=${Date.now()}`;

                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                        try {
                            if (krpano.get(`hotspot[${hotspotName}]`)) {
                                krpano.set(`hotspot[${hotspotName}].url`, retryUrl);
                            }
                        } catch (e) { /* ignore */ }
                    };
                    img.onerror = () => {
                        try {
                            if (krpano.get(`hotspot[${hotspotName}]`)) {
                                krpano.set(`hotspot[${hotspotName}].url`, retryUrl);
                            }
                        } catch (e) { /* ignore */ }
                    };
                    img.src = cleanUrl;

                    console.log(`[MultimediaManager] Retry #${retryCount}/${MAX_RETRIES} loading icon for ${hotspotName}`);
                }
            } catch (e) {
                return;
            }

            setTimeout(retryLoad, RETRY_DELAY);
        };

        setTimeout(retryLoad, RETRY_DELAY);
    }, [krpano]);

    const renderHotspot = useCallback(async (hotspot: any, skipExistCheck: boolean = false) => {
        const hotspotName = `hotspot_${hotspot.id}`;
        const textHotspotName = `${hotspotName}_text`;

        if (!krpano) {
            return;
        }

        // Always remove existing text hotspot first to prevent duplicates
        try {
            krpano.call(`removehotspot(${textHotspotName})`);
        } catch {
            // Ignore errors if text hotspot doesn't exist
        }

        // Skip exist check if we're doing a force reload (hotspot was just removed)
        if (!skipExistCheck && krpano.get(`hotspot[${hotspotName}]`)) {
            // Even if we skip re-rendering the main hotspot, still create text label if needed
            // (since we removed it above)
            const shouldHideIcon = hotspot.display_type === 'name-only';
            try {
                krpano.set(`hotspot[${hotspotName}].alpha`, shouldHideIcon ? 0 : 1);
                krpano.set(`hotspot[${hotspotName}].visible`, true);
                krpano.set(`hotspot[${hotspotName}].enabled`, true);
            } catch {
                // Ignore errors if hotspot is in a temporary state
            }

            const shouldShowLabel = hotspot.style_hover !== 'callout' &&
                hotspot.style_hover !== 'hide-displayname' &&
                hotspot.display_type !== 'icon-only';
            if (shouldShowLabel && hotspot.title) {
                const coordinate = hotspot.coordinate && hotspot.coordinate[0] ? hotspot.coordinate[0] : { ath: 0, atv: 0 };
                createTextLabelHotspot(krpano, textHotspotName, hotspot, coordinate);

                if (hotspot.display_type === 'name-only') {
                    krpano.set(`hotspot[${textHotspotName}].enabled`, true);
                    krpano.set(`hotspot[${textHotspotName}].capture`, true);
                    krpano.set(`hotspot[${textHotspotName}].handcursor`, true);
                    krpano.set(`hotspot[${textHotspotName}].onclick`, `js(window.showMultimediaPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);
                }
            }
            return;
        }

        // Extract coordinate first
        const coordinate = hotspot.coordinate && hotspot.coordinate[0] ? hotspot.coordinate[0] : { ath: 0, atv: 0 };

        // add hotspot
        krpano.call(`addhotspot(${hotspotName})`);

        // Map different styles to available styles
        let styleToLoad = hotspot.style || 'hotspot_default_style';

        // Check if style already has the correct format (hotspot_XXX_style)
        // If not, add prefix and suffix
        if (!styleToLoad.startsWith('hotspot_') || !styleToLoad.endsWith('_style')) {
            // Old format: just the name (e.g., 'default', 'information')
            styleToLoad = `hotspot_${styleToLoad}_style`;
        }
        // If it already has the format (e.g., 'hotspot_default_style'), use as-is

        try {
            krpano.call(`hotspot[${hotspotName}].loadstyle(${styleToLoad})`);
        } catch {
            // Try with default style as fallback
            try {
                krpano.call(`hotspot[${hotspotName}].loadstyle(hotspot_default_style)`);
            } catch {
                // Ignore
            }
        }

        // IMPORTANT: set scale AFTER loadstyle() because loadstyle may reset scale.
        // Use a robust approach: set immediately, call a tween as backup, and set again after a longer delay.
        // Apply scale robustly via a helper function to avoid loadstyle/plugin race conditions
        try {
            const applyEffectiveScale = (value?: number) => setHotspotScale(hotspotName, value);

            const hasScale = hotspot.scale !== undefined && hotspot.scale !== null;
            const hasMobileScale = hotspot.scale_mobile !== undefined && hotspot.scale_mobile !== null;
            let effectiveScale: number | undefined = undefined;

            if (typeof window !== 'undefined' && window.innerWidth <= 768 && hasMobileScale) {
                effectiveScale = Number(hotspot.scale_mobile);
            } else if (hasScale) {
                effectiveScale = Number(hotspot.scale);
            }
            if (effectiveScale !== undefined && !Number.isNaN(effectiveScale)) applyEffectiveScale(effectiveScale);
            else console.warn(`[MultimediaManager] No valid scale to apply for ${hotspotName}: hasScale=${hasScale}, hasMobileScale=${hasMobileScale}`);
        } catch (scaleError) {
            console.warn(`[MultimediaManager] Could not set scale for ${hotspotName}:`, scaleError);
        }

        // if (hotspot.style_hover !== 'callout') {
        //     krpano.call(`hotspot[${hotspotName}].loadstyle(hotspot_${styleToLoad}_style)`);
        // } else {
        //     krpano.call('create_hs_back(' + hotspot.target_scene_name + ', hotspot_' + styleToLoad + '_style, ' + coordinate.ath + ',' + coordinate.atv + ', ' + hotspot.title + ', ' + hotspot.title + ', ' + hotspotName + ');');
        // }

        // add ath, atv
        krpano.set(`hotspot[${hotspotName}].ath`, coordinate.ath);
        krpano.set(`hotspot[${hotspotName}].atv`, coordinate.atv);

        // add name hotspot
        const showtext = hotspot.title;
        krpano.set(`hotspot[${hotspotName}].onhover`, `showtext("${showtext}"); `);

        // Keep fixed size when zooming, matching 3D navigation and avoiding callout drift
        krpano.set(`hotspot[${hotspotName}].distorted`, false);
        krpano.set(`hotspot[${hotspotName}].zoom`, false);
        krpano.set(`hotspot[${hotspotName}].zorder`, 100); // Higher than Image2D (zorder=1)

        // Make sure hotspot is visible and enabled
        krpano.set(`hotspot[${hotspotName}].visible`, true);
        krpano.set(`hotspot[${hotspotName}].enabled`, true);

        // Name-only mode keeps hotspot clickable but hides icon visual.
        const shouldHideIcon = hotspot.display_type === 'name-only';
        krpano.set(`hotspot[${hotspotName}].alpha`, shouldHideIcon ? 0 : 1);

        // Create text label hotspot for title (only if not hidden and display_type is 'both')
        // Check style_hover and display_type to determine if we should show the label
        const shouldShowLabel = hotspot.style_hover !== 'callout' &&
            hotspot.style_hover !== 'hide-displayname' &&
            hotspot.display_type !== 'icon-only';

        if (shouldShowLabel && hotspot.title) {
            // Use helper function to create text label (pass hotspot for config-based styling)
            createTextLabelHotspot(krpano, textHotspotName, hotspot, coordinate);

            if (hotspot.display_type === 'name-only') {
                krpano.set(`hotspot[${textHotspotName}].enabled`, true);
                krpano.set(`hotspot[${textHotspotName}].capture`, true);
                krpano.set(`hotspot[${textHotspotName}].handcursor`, true);
                krpano.set(`hotspot[${textHotspotName}].onclick`, `js(window.showMultimediaPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);
            }
        }

        // hiển thị thông ti hotspot
        //     if (hotspot.style_hover != 'callout' && hotspot.style_hover != 'hide-displayname') {
        //         krpano.call('addhotspot(' + hotspotName + '_invisible)');

        //         krpano.set('hotspot[' + hotspotName + '_invisible].html', showtext);
        //         krpano.set('hotspot[' + hotspotName + '_invisible].ath', coordinate.ath);
        //         krpano.set('hotspot[' + hotspotName + '_invisible].atv', coordinate.atv * 1 + 2);
        //         const widthText = isTextWidthExceeds(hotspot.title);
        //         krpano.set('hotspot[' + hotspotName + '_invisible].width', widthText ? '140' : 'auto');
        //         krpano.set('hotspot[' + hotspotName + '_invisible].wordwrap', 'true');

        //         krpano.set(
        //             'hotspot[' + hotspotName + '_invisible].css',
        //             `color:#264755; 
        //                                  font-family: &#34;Montserrat &#34;,	&#34;Lato	&#34;,sans-serif;
        //                                  font-size:12px;
        //                                  font-weight:bold;
        //                                  text-align:center; 
        //                                  background-color: white;
        //                                  padding:2px;
        //                                  height:auto;
        //                                  width: auto;
        //                                  max-width: 140px;
        //                                  border-radius: 10px;
        //                                  border:solid 0.2px #F6E493;
        //                                  word-wrap: break-word;
        //                                  overflow-wrap: break-word;
        //                                  white-space: normal;
        // `
        //         );

        //         krpano.set('hotspot[' + hotspotName + '_invisible].txtshadow', '2 2 60 0x014689 4.5');
        //         krpano.set('hotspot[' + hotspotName + '_invisible].background', 'true');
        //         krpano.set('hotspot[' + hotspotName + '_invisible].backgroundalpha', '1');
        //         krpano.set('hotspot[' + hotspotName + '_invisible].bgroundedge', '8');
        //         krpano.set('hotspot[' + hotspotName + '_invisible].border', 'true');
        //         krpano.set('hotspot[' + hotspotName + '_invisible].bordercolor', '0xf6e493');
        //         krpano.set('hotspot[' + hotspotName + '_invisible].borderwidth', '3.0');
        //         krpano.set('hotspot[' + hotspotName + '_invisible].roundedge', '8');
        //         krpano.set('hotspot[' + hotspotName + '_invisible].url', 'textfield.swf');
        //     } else {
        //         krpano.set('layer[' + hotspotName + '_text].html', hotspot.title || '');
        //     }

        const name_layer_thumbnail = hotspotName + '_thumbnail';
        const ath = coordinate.ath;
        const atv = coordinate.atv;

        // Use a default thumbnail if none provided
        // const thumbnail_hotspot = hotspot.thumbnail || '/images/DefaultBackground.png';

        // krpano.call(`add_thumbnail_hotspot(${name_layer_thumbnail}, ${ath}, ${atv}, 0, ${thumbnail_hotspot}, ${hotspotName}, ${hotspot.target_scene_name || currentScene})`);

        // krpano.set("hotspot[" + hs_name + "].onhover", "showtext(" + showtext + "); ");
        // krpano.set(`hotspot[${hotspotName}].onover`, `show_zoom_image(${name_layer_thumbnail});`);

        // krpano.set(`hotspot[${hs_name}].onover`,`call(hotspot[${hs_name}].loadstyle(thumbnail_hotspot)); set(hotspot[${hs_name}].url,${thumbnail_hotspot})`)

        // krpano.set(`hotspot[${hs_name}].onover`,`show_zoom_image(${hs_name})`)
        // krpano.set(`hotspot[${hotspotName}].onout`, `hide_zoom_image(${name_layer_thumbnail})`);

        // krpano.set(`hotspot[${hotspotName}].onclick`, `js(window.showMultimediaHotspotPopup('${hotspot.entity_id}'))`);
        krpano.set(`hotspot[${hotspotName}].onclick`, `js(window.showMultimediaPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);

        // if (hotspot.type_destination == 'group') {
        //     if (typeof window !== 'undefined' && window.fetchListByGroupId) {
        //         krpano.set(`hotspot[${hotspotName}].onclick`, `js(window.fetchListByGroupId('${hotspot.link?.toString() || ''}'))`);
        //         krpano.set(`layer[${name_layer_thumbnail}].onclick`, `js(window.fetchListByGroupId('${hotspot.link?.toString() || ''}'));`);
        //     }
        // }
        // // assign onclick event for hotspot
        // else if (hotspot.target_scene_name) {
        //     krpano.set(`hotspot[${hotspotName}].onclick`, `js(navigateSceneFromHotspot('${hotspot.target_scene_name}'))`);
        //     krpano.set(`hotspot[${hotspotName}_invisible].onclick`, `js(navigateSceneFromHotspot('${hotspot.target_scene_name}'))`);
        //     krpano.set(`layer[${name_layer_thumbnail}].onclick`, `js(window.navigateSceneFromHotspot('${hotspot.target_scene_name}'));`);
        //     krpano.set(`layer[${hotspotName}_text].onclick`, `js(window.navigateSceneFromHotspot('${hotspot.target_scene_name}'));`);
        // }

        // Ensure icon loads reliably with automatic retry on failure
        ensureHotspotIconLoaded(hotspotName);
    }, [krpano, setHotspotScale, createTextLabelHotspot, ensureHotspotIconLoaded]);

    // Keep saved IoT callouts visible on the Admin canvas even when the edit
    // form is closed. The edit form owns its temporary preview while open.
    useEffect(() => {
        if (!krpano) return;

        const removeCallout = (hotspotId: string) => {
            try { krpano.call(`removelayer(${getAdminIoTCalloutPanelName(hotspotId)})`); } catch { /* already removed */ }
            try { krpano.call(`removehotspot(${getAdminIoTCalloutTraitName(hotspotId)})`); } catch { /* already removed */ }
        };

        if (isUpdateFormOpen || isCreateFormOpen) {
            adminIoTCalloutIdsRef.current.forEach(removeCallout);
            adminIoTCalloutIdsRef.current.clear();
            return;
        }

        const renderCallouts = () => {
            const activeIds = new Set<string>();
            adminIoTCalloutSources.forEach((source) => {
                const hotspotId = String(source.hotspot.id);
                const primaryName = source.hotspot.type === 'hotspot_3d'
                    ? `hotspot_${hotspotId}_multimedia`
                    : `hotspot_${hotspotId}`;
                const visualName = findKrpanoVisualHotspotName(krpano, [
                    primaryName,
                    `hotspot_${hotspotId}_multimedia`,
                    `hotspot_${hotspotId}`,
                ]);
                const anchor = getHotspotAnchor(source.hotspot.coordinate || source.hotspot.coordinates);
                const athValue = visualName ? krpano.get(`hotspot[${visualName}].ath`) : anchor?.ath;
                const atvValue = visualName ? krpano.get(`hotspot[${visualName}].atv`) : anchor?.atv;
                const ath = Number(athValue);
                const atv = Number(atvValue);
                if (!Number.isFinite(ath) || !Number.isFinite(atv)) return;

                const localized = localizeIoTConfig(source.config, currentLanguage?.id);
                const rows = getIoTCalloutRows(
                    localized,
                    iotCalloutDataByDeviceId[String(localized.deviceId)],
                ).slice(0, 4);
                const title = localized.calloutTitle || source.hotspot.title || localized.deviceName || 'Thiết bị IoT';
                const content = buildIoTKrpanoCalloutContent(title, rows);
                upsertIoTKrpanoCallout(krpano, {
                    traitName: getAdminIoTCalloutTraitName(hotspotId),
                    panelName: getAdminIoTCalloutPanelName(hotspotId),
                    ath,
                    atv,
                    connectorY: (visualName ? getKrpanoHotspotTopY(krpano, visualName, 20, source.hotspot.scale) : -20) + source.offsetY,
                    traitHeight: source.traitHeight,
                    panelHeight: content.panelHeight,
                    html: content.html,
                    layoutKey: content.layoutKey,
                });
                activeIds.add(hotspotId);
            });

            adminIoTCalloutIdsRef.current.forEach((hotspotId) => {
                if (!activeIds.has(hotspotId)) removeCallout(hotspotId);
            });
            adminIoTCalloutIdsRef.current = activeIds;
        };

        renderCallouts();
        const timers = [250, 800].map((delay) => window.setTimeout(renderCallouts, delay));
        return () => timers.forEach((timer) => window.clearTimeout(timer));
    }, [
        adminIoTCalloutSources,
        currentLanguage?.id,
        iotCalloutDataByDeviceId,
        isCreateFormOpen,
        isUpdateFormOpen,
        krpano,
    ]);

    useEffect(() => {
        if (!idPath || !adminIoTDeviceKey) return;
        const deviceIds = adminIoTDeviceKey.split(',').filter(Boolean);
        const socket = createSocketIoT();
        const join = () => {
            socket.emit('joinProject', { idPath });
            deviceIds.forEach((deviceId) => socket.emit('joinDevice', { deviceId }));
        };
        const receive = (payload: IoTRealtimeDeviceData) => {
            const deviceId = String(payload.device_id);
            if (!deviceIds.includes(deviceId)) return;
            setIoTCalloutDataByDeviceId((current) => ({ ...current, [deviceId]: payload }));
        };
        if (socket.connected) join();
        socket.on('connect', join);
        socket.on('device:data', receive);
        return () => {
            socket.off('connect', join);
            socket.off('device:data', receive);
            socket.disconnect();
        };
    }, [adminIoTDeviceKey, idPath]);

    useEffect(() => {
        const instance = krpano;
        return () => {
            if (!instance) return;
            adminIoTCalloutIdsRef.current.forEach((hotspotId) => {
                try { instance.call(`removelayer(${getAdminIoTCalloutPanelName(hotspotId)})`); } catch { /* already removed */ }
                try { instance.call(`removehotspot(${getAdminIoTCalloutTraitName(hotspotId)})`); } catch { /* already removed */ }
            });
            adminIoTCalloutIdsRef.current.clear();
        };
    }, [krpano]);

    const renderPolygonHotspots = useCallback((hotspot: any) => {
        const hotspotName = `hotspot_${hotspot.id}_multimedia`;

        if (!krpano) {
            console.warn('[MultimediaManager] krpano is not available when adding polygon hotspot:', hotspotName);
            return;
        }

        if (krpano.get(`hotspot[${hotspotName}]`)) {
            // Re-create existing hotspot to avoid stale state (missing onclick/capture after create flow).
            try {
                krpano.call(`removehotspot(${hotspotName})`);
                krpano.call(`removehotspot(${hotspotName}_text)`);
            } catch (e) {
                console.warn(`[MultimediaManager] Failed removing existing polygon hotspot ${hotspotName}:`, e);
            }
        }


        if (hotspot.type == 'hotspot_3d' || hotspot.type == 'hotspot3d') {
            let parsedCfg: any = null;
            if (typeof hotspot.config === 'string') {
                try { parsedCfg = JSON.parse(hotspot.config); } catch {}
            } else if (typeof hotspot.config === 'object' && hotspot.config) {
                parsedCfg = hotspot.config;
            }

            const isModernOverlay3D = parsedCfg && (
                parsedCfg.depth !== undefined ||
                parsedCfg.model_url ||
                parsedCfg.asset_uuid
            );

            if (isModernOverlay3D) {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('sg3d_reload_multimedia'));
                }
                return;
            }

            // Legacy 3D model hotspot in Admin
            if (!krpano) {
                console.warn('[Hotspot] Krpano instance not available for rendering threejs hotspots');
                return;
            }

            if (krpano.get('plugin[threejs]')) {
                krpano.call('removeplugin(threejs)');
            }

            krpano.call(`addplugin(threejs); set(plugin[threejs].url, 'https://sanpham.starglobal3d.vn/managements/krpano/krpano-1.23.1/plugins/threejs_krpanoplugin.js'); set(plugin[threejs].useembeddedthreejs, true); set(plugin[threejs].integratedrendering, true); set(plugin[threejs].es6modules, false); set(plugin[threejs].adjustlightfalloff, true); set(plugin[threejs].cache, true); set(plugin[threejs].keep, true);`);

            const threejsHotspots = [hotspotName, 'light1', 'light2', 'light3', 'light6'];
            threejsHotspots.forEach((name) => {
                if (krpano.get(`hotspot[${name}]`)) {
                    krpano.call(`removehotspot(${name})`);
                }
            });

            krpano.call(`addhotspot(${hotspotName})`);
            krpano.set(`hotspot[${hotspotName}].type`, 'threejs');
            krpano.set(`hotspot[${hotspotName}].url`, hotspot.entity_id);

            try {
                const hasScale = hotspot.scale !== undefined && hotspot.scale !== null;
                const hasMobileScale = hotspot.scale_mobile !== undefined && hotspot.scale_mobile !== null;
                let effectiveScale: number | undefined = undefined;

                if (typeof window !== 'undefined' && window.innerWidth <= 768 && hasMobileScale) {
                    effectiveScale = Number(hotspot.scale_mobile);
                } else if (hasScale) {
                    effectiveScale = Number(hotspot.scale);
                } else {
                    effectiveScale = 1;
                }

                if (effectiveScale !== undefined && !Number.isNaN(effectiveScale)) {
                    setHotspotScale(hotspotName, effectiveScale);
                }
            } catch (e) {
                console.warn(`[MultimediaManager] Failed to set 3D hotspot scale for ${hotspotName}`, e);
            }

            krpano.set(`hotspot[${hotspotName}].rotation`, 'enable');
            const coord = (hotspot.coordinate && hotspot.coordinate[0]) ? hotspot.coordinate[0] : {};
            krpano.set(`hotspot[${hotspotName}].rx`, coord.rx || 75);
            krpano.set(`hotspot[${hotspotName}].ry`, coord.ry || 0.0);
            krpano.set(`hotspot[${hotspotName}].rz`, coord.rz || 0.0);
            krpano.set(`hotspot[${hotspotName}].rotationorder`, 'yxz');
            krpano.set(`hotspot[${hotspotName}].depth`, 0);
            krpano.set(`hotspot[${hotspotName}].tx`, coord.tx || 0);
            krpano.set(`hotspot[${hotspotName}].ty`, coord.ty || 0);
            krpano.set(`hotspot[${hotspotName}].tz`, coord.tz || 1050);
            krpano.set(`hotspot[${hotspotName}].hittest`, false);
            krpano.set(`hotspot[${hotspotName}].enabled`, false);
            krpano.set(`hotspot[${hotspotName}].visible`, false);
            krpano.set(`hotspot[${hotspotName}].onloaded`, `init_animations(${hotspotName}); call(hotspot[${hotspotName}].animations[0].play);`);

            const lights = [
                { name: `light1_${hotspot.id}`, ath: 0, atv: -30, intensity: 1.0 },
                { name: `light2_${hotspot.id}`, ath: 0, atv: -30, intensity: 1.0 },
                { name: `light3_${hotspot.id}`, ath: 0, atv: -30, intensity: 1.0 },
                { name: `light6_${hotspot.id}`, ath: 10, atv: -120, intensity: 0.5 },
            ];
            lights.forEach((light) => {
                krpano.call(`addhotspot(${light.name})`);
                krpano.set(`hotspot[${light.name}].type`, 'threejslight');
                krpano.set(`hotspot[${light.name}].mode`, 'sun');
                krpano.set(`hotspot[${light.name}].intensity`, light.intensity);
                krpano.set(`hotspot[${light.name}].color`, '0xFFFFFF');
                krpano.set(`hotspot[${light.name}].ath`, light.ath);
                krpano.set(`hotspot[${light.name}].atv`, light.atv);
            });

            krpano.call(`
                delayedcall(0.5, 
                    set(hotspot[${hotspotName}].visible, true);
                    call(hotspot[${hotspotName}].animations[0].play);
                );
            `);
            return;
        }

        // add polygon hotspot
        krpano.call(`addhotspot(${hotspotName})`);

        // Handle regular polygon hotspots
        // Must load the style first for polygon to work properly
        krpano.call(`hotspot[${hotspotName}].loadstyle(blink_polygonhotspot)`);

            // Override the style properties after loading to control visibility
            krpano.set(`hotspot[${hotspotName}].zorder`, 5);

            // Apply display polygon type styling
                       const displayType = hotspot.display_polygon_type || 'default';
            const borderWidth = hotspot.border_width !== undefined && hotspot.border_width !== null ? hotspot.border_width : 3;
            const fillAlpha = hotspot.color_fill ? (hotspot.color_fill_alpha !== undefined && hotspot.color_fill_alpha !== null ? hotspot.color_fill_alpha : 0.5) : 0;


            krpano.set('hotspot[' + hotspotName + '].bordercolor', hotspot.color_line || '0xffffff');
            krpano.set('hotspot[' + hotspotName + '].fillcolor', hotspot.color_fill || '0xffffff');
            krpano.set(`hotspot[${hotspotName}].borderwidth`, borderWidth);

            // Override the blink action to respect the custom border width
            // We redefine the action specifically for this hotspot to oscillate around the custom width
            // instead of hardcoded 3 and 0.3
            const blinkActionName = `polygonhotspot_blink_${hotspot.id}`;
            const minWidth = Math.max(0.1, borderWidth * 0.5); // 50% of width
            const maxWidth = borderWidth * 1.5; // 150% of width

            // Define a custom blink action for this specific hotspot
            // Note: We use add_action to dynamically create the action in krpano
            // The action definition needs to be properly formatted for krpano parsing
            const actionContent = `
                tween(hotspot[${hotspotName}].borderwidth, ${maxWidth}, 1);
                delayedcall(1, tween(hotspot[${hotspotName}].borderwidth, ${minWidth}, 1));
                delayedcall(2, ${blinkActionName}());
            `;

            // Use krpano.set to define the action content directly
            krpano.set(`action[${blinkActionName}].content`, actionContent);

            // Set the onloaded event to use our custom blink action
            // We need to ensure the action exists before calling it
            krpano.set(`hotspot[${hotspotName}].onloaded`, `${blinkActionName}();`);

            krpano.set(`hotspot[${hotspotName}].borderalpha`, 1);
            krpano.set(`hotspot[${hotspotName}].fillalpha`, fillAlpha);

            // Add all polygon points (filtering out duplicate coordinates)
            if (hotspot.coordinate && Array.isArray(hotspot.coordinate)) {
                const uniqueCoordinates = [];
                const seenPoints = new Set();
                for (const coord of hotspot.coordinate) {
                    if (coord) {
                        const ath = coord.ath !== undefined && coord.ath !== null ? Number(coord.ath) : 0;
                        const atv = coord.atv !== undefined && coord.atv !== null ? Number(coord.atv) : 0;
                        const key = `${ath.toFixed(4)},${atv.toFixed(4)}`;
                        if (!seenPoints.has(key)) {
                            seenPoints.add(key);
                            uniqueCoordinates.push(coord);
                        }
                    }
                }
                for (let j = 0; j < uniqueCoordinates.length; j++) {
                    krpano.set(`hotspot[${hotspotName}].point[${j}].ath`, uniqueCoordinates[j].ath);
                    krpano.set(`hotspot[${hotspotName}].point[${j}].atv`, uniqueCoordinates[j].atv);
                }
            }

            // Set onclick action
            krpano.set(`hotspot[${hotspotName}].onclick`, `js(window.showMultimediaPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);

            // Parse hover color with alpha channel support
            const hoverColor = hotspot.color_fill_hover;
            let hoverFillColor = hotspot.color_fill ?? 0xffffff;
            let hoverFillAlpha = 0;

            if (hoverColor) {
                // If color_fill_hover is provided, use it for both color and alpha
                if (typeof hoverColor === 'string' && hoverColor.startsWith('0x') && hoverColor.length === 10) {
                    // Format: 0xFFFFFFAA (color + alpha)
                    hoverFillColor = hoverColor.substring(0, 8); // 0xFFFFFF
                    hoverFillAlpha = parseInt(hoverColor.substring(8), 16) / 255; // AA -> 0-1
                } else if (typeof hoverColor === 'string' && hoverColor.startsWith('#') && hoverColor.length === 9) {
                    // Format: #FFFFFFAA (color + alpha)
                    hoverFillColor = '0x' + hoverColor.substring(1, 7); // #FFFFFF -> 0xFFFFFF
                    hoverFillAlpha = parseInt(hoverColor.substring(7), 16) / 255; // AA -> 0-1
                } else {
                    // Fallback: use as color only with default alpha
                    hoverFillColor = hoverColor;
                    hoverFillAlpha = hotspot.color_fill_alpha_hover ?? 0.5;
                }
            } else {
                // Fallback to separate alpha value if available
                hoverFillColor = '0xffffff';
                hoverFillAlpha = 0.5;
            }

            // add name hotspot and hover effects
            const showtext = toKrpanoTooltipText(hotspot.title || hotspot.name_vn || 'Polygon');

            // Check if we should show text label (matching logic from renderHotspot for 2D hotspots)
            const shouldShowLabel = hotspot.style_hover !== 'callout' &&
                hotspot.style_hover !== 'hide-displayname' &&
                hotspot.display_type !== 'icon-only';

            // Parse text_position để xác định vị trí text (tham khảo từ trang sản phẩm)
            let textPosition: { ath: number; atv: number; edge?: string; ox?: number; oy?: number };

            // Kiểm tra nếu text_position là object với tọa độ (ath, atv)
            if (typeof hotspot.text_position === 'object' && hotspot.text_position?.ath !== undefined) {
                // Sử dụng trực tiếp tọa độ từ text_position
                textPosition = {
                    ath: parseFloat(hotspot.text_position.ath),
                    atv: parseFloat(hotspot.text_position.atv),
                    edge: 'center',
                    ox: 0,
                    oy: 0
                };
            } else if (typeof hotspot.text_position === 'string') {
                // Thử parse JSON string nếu là string
                let parsed = null;

                try {
                    // Thử parse trực tiếp
                    parsed = JSON.parse(hotspot.text_position);
                } catch (e1) {
                    // Nếu parse lỗi, thử fix thiếu dấu đóng ngoặc
                    try {
                        // Kiểm tra nếu string bắt đầu với { và có ath/atv nhưng thiếu }
                        if (hotspot.text_position.startsWith('{') && hotspot.text_position.includes('ath') && !hotspot.text_position.endsWith('}')) {
                            const fixedJson = hotspot.text_position + '}';
                            parsed = JSON.parse(fixedJson);
                        }
                    } catch (e2) {
                        // Ignore
                    }
                }

                if (parsed && parsed.ath !== undefined && parsed.atv !== undefined) {
                    // Nếu parse thành công và có tọa độ, sử dụng trực tiếp
                    textPosition = {
                        ath: parseFloat(parsed.ath),
                        atv: parseFloat(parsed.atv),
                        edge: 'center',
                        ox: 0,
                        oy: 0
                    };
                } else {
                    // Nếu không phải tọa độ, tính toán dựa trên vị trí (inside-center, outside-top-left, etc.)
                    if (hotspot.coordinate && Array.isArray(hotspot.coordinate) && hotspot.coordinate.length > 0) {
                        textPosition = calculateTextPosition(
                            hotspot.coordinate,
                            hotspot.text_position || 'inside-center'
                        );
                    } else {
                        // Fallback nếu không có coordinate
                        textPosition = { ath: 0, atv: 0, edge: 'center', ox: 0, oy: 0 };
                    }
                }
            } else {
                // Fallback: tính toán dựa trên vị trí mặc định
                if (hotspot.coordinate && Array.isArray(hotspot.coordinate) && hotspot.coordinate.length > 0) {
                    textPosition = calculateTextPosition(
                        hotspot.coordinate,
                        'inside-center'
                    );
                } else {
                    // Fallback cuối cùng nếu không có coordinate
                    textPosition = { ath: 0, atv: 0, edge: 'center', ox: 0, oy: 0 };
                }
            }

            // Create text label hotspot for polygon (only if display_type is 'both')
            const textHotspotName = `${hotspotName}_text`;
            if (shouldShowLabel && hotspot.title) {
                // Use the same helper function as 2D hotspots for consistent styling
                createTextLabelHotspot(krpano, textHotspotName, hotspot, textPosition);
                // Enable click and set onclick on the label to show popup in Admin
                krpano.set(`hotspot[${textHotspotName}].enabled`, true);
                krpano.set(`hotspot[${textHotspotName}].capture`, true);
                krpano.set(`hotspot[${textHotspotName}].handcursor`, true);
                krpano.set(`hotspot[${textHotspotName}].onclick`, `js(window.showMultimediaPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);
            }

            // Set hover events for polygon hotspots (only show hover text if icon-only mode)
            const shouldShowHoverText = hotspot.display_type === 'icon-only';
            const hoverCommand = shouldShowHoverText
                ? `showtext("${showtext}"); set(hotspot[${hotspotName}].fillcolor, ${hoverFillColor}); set(hotspot[${hotspotName}].fillalpha, ${hoverFillAlpha});`
                : `set(hotspot[${hotspotName}].fillcolor, ${hoverFillColor}); set(hotspot[${hotspotName}].fillalpha, ${hoverFillAlpha});`;

            krpano.set(`hotspot[${hotspotName}].onhover`, hoverCommand);
            krpano.set(`hotspot[${hotspotName}].onout`, `set(hotspot[${hotspotName}].fillcolor, ${hotspot.color_fill ?? 0xffffff}); set(hotspot[${hotspotName}].fillalpha, ${fillAlpha});`);

            // Apply scale for polygons if provided, using the robust helper
            try {
                const hasScale = hotspot.scale !== undefined && hotspot.scale !== null;
                const hasMobileScale = hotspot.scale_mobile !== undefined && hotspot.scale_mobile !== null;
                let effectiveScale: number | undefined = undefined;
                if (typeof window !== 'undefined' && window.innerWidth <= 768 && hasMobileScale) {
                    effectiveScale = Number(hotspot.scale_mobile);
                } else if (hasScale) {
                    effectiveScale = Number(hotspot.scale);
                }
                if (effectiveScale !== undefined && !Number.isNaN(effectiveScale)) setHotspotScale(hotspotName, effectiveScale);
            } catch (e) {
                console.warn(`[MultimediaManager] Could not set polygon scale for ${hotspotName}:`, e);
            }

        krpano.set(`hotspot[${hotspotName}].hovering`, false);
        krpano.set(`hotspot[${hotspotName}].enabled`, true);
        krpano.set(`hotspot[${hotspotName}].capture`, true);

    }, [krpano, setHotspotScale, createTextLabelHotspot]);

    const fetchMultimedia = useCallback(async (sceneName: string, forceReload: boolean = false) => {
        setIsLoading(true);

        try {
            // If forceReload, remove all existing hotspots for this scene first
            if (forceReload && krpano && hotspotDataList.length > 0) {
                hotspotDataList.forEach((hotspot: any) => {
                    try {
                        const hotspotName = `hotspot_${hotspot.id}`;
                        const polygonName = `hotspot_${hotspot.id}_multimedia`;
                        krpano.call(`removehotspot(${hotspotName})`);
                        krpano.call(`removehotspot(${polygonName})`);
                        // Remove text hotspots
                        krpano.call(`removehotspot(${hotspotName}_text)`);
                        krpano.call(`removehotspot(${polygonName}_text)`);
                    } catch (e) {
                        // Ignore errors for hotspots that don't exist
                    }
                });
            }

            const res = await multimediaServices.getAllMultimediaBySceneName({ sceneName, id_path: idPath });
            if (!res || !res.data) {
                setHotspotDataList([]);
                return;
            }
            const polygonHotspots = res.data.data;

            if (polygonHotspots && polygonHotspots.length > 0) {
                // Store the multimedia data for popup use
                setHotspotDataList(polygonHotspots);

                polygonHotspots.forEach((polygonHotspot: any) => {
                    // Render based on type: polygon, hotspot_3d, audio, or regular hotspot
                    // Pass forceReload to skip exist check when forcing reload
                    // Check for audio by type, entity_type, or link_type (for backward compatibility)
                    const isAudio = polygonHotspot.type === 'audio' || polygonHotspot.entity_type === 'audio' || polygonHotspot.link_type === 'Âm thanh';

                    if (polygonHotspot.type === 'polygon') {
                        renderPolygonHotspots(polygonHotspot);
                    } else if (polygonHotspot.type === 'hotspot_3d') {
                        renderPolygonHotspots(polygonHotspot);
                    } else if (isAudio) {
                        // Audio type - render as hotspot icon
                        renderHotspot(polygonHotspot, forceReload);
                    } else if (polygonHotspot.type === 'hotspot') {
                        renderHotspot(polygonHotspot, forceReload);
                    } else if (polygonHotspot.type === 'textbox' || polygonHotspot.entity_type === 'textbox') {
                        renderTextbox(polygonHotspot);
                    } else if (polygonHotspot.type === 'image2d' || polygonHotspot.entity_type === 'image2d') {
                        renderImage2D(polygonHotspot);
                    } else if (polygonHotspot.type === 'video2d' || polygonHotspot.entity_type === 'video2d') {
                        renderVideo2D(polygonHotspot);
                    } else {
                        // Unknown type, render as hotspot
                        renderHotspot(polygonHotspot, forceReload);
                    }
                });
            } else {
                setHotspotDataList([]);
            }
        } catch (error) {
            console.error('[MultimediaManager] Error fetching hotspots:', error);
        } finally {
            setIsLoading(false);
        }
    }, [idPath, renderHotspot, renderPolygonHotspots, renderTextbox, renderImage2D, renderVideo2D, currentScene, krpano, hotspotDataList]);

    const isTextWidthExceeds = (text: string, font: string = '16px Montserrat', maxWidth: number = 140): boolean => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return false;

        context.font = font;
        const textWidth = context.measureText(text).width;

        return textWidth > maxWidth;
    };

    // Update multimedia function
    const updateMultimedia = useCallback(async (id: number, data: any) => {
        if (!idPath) {
            console.warn('[MultimediaManager] No idPath available for update');
            return;
        }

        try {
            setIsLoading(true);

            const response = await multimediaServices.updateMultimedia({
                id_path: idPath,
                id: id.toString(),
                body: data
            });

            if (response && response.success) {
                // Refresh the current scene to show updated data
                if (currentScene) {
                    await fetchMultimedia(currentScene);
                }
            } else {
                console.error('❌ Failed to update multimedia:', response);
                throw new Error(response?.message || 'Update failed');
            }
        } catch (error) {
            console.error('[MultimediaManager] Error updating multimedia:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [idPath, currentScene, fetchMultimedia]);

    // Delete multimedia function
    const deleteMultimedia = useCallback(async (id: number) => {
        if (!idPath) {
            console.warn('[MultimediaManager] No idPath available for delete');
            return;
        }

        try {
            setIsLoading(true);

            const response = await multimediaServices.deleteMultimedia({
                id_path: idPath,
                id
            });

            if (response && response.success) {

                // Remove from krpano scene
                if (krpano) {
                    const hotspotName = `hotspot_${id}_multimedia`;
                    const regularHotspotName = `hotspot_${id}`;

                    try {
                        // Try both naming conventions
                        krpano.call(`removehotspot(${hotspotName})`);
                        krpano.call(`removehotspot(${regularHotspotName})`);

                        // Remove text label hotspots (important: these are created with _text suffix)
                        krpano.call(`removehotspot(${hotspotName}_text)`);
                        krpano.call(`removehotspot(${regularHotspotName}_text)`);

                        // Also remove any associated lights for 3D models
                        const lights = [`light1_${id}`, `light2_${id}`, `light3_${id}`, `light6_${id}`];
                        lights.forEach(lightName => {
                            try {
                                krpano.call(`removehotspot(${lightName})`);
                            } catch (e) {
                                // Ignore errors for lights that don't exist
                            }
                        });

                    } catch (krpanoError) {
                        console.warn('[MultimediaManager] Error removing hotspot from krpano:', krpanoError);
                    }
                }

                // Refresh the current scene to remove deleted data
                if (currentScene) {
                    await fetchMultimedia(currentScene);
                }
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('sg3d_reload_multimedia'));
                }
            } else {
                console.error('❌ Failed to delete multimedia:', response);
                throw new Error(response?.message || 'Delete failed');
            }
        } catch (error) {
            console.error('[MultimediaManager] Error deleting multimedia:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [idPath, currentScene, fetchMultimedia, krpano]);

    useEffect(() => {
        const activeScene = String(krpano?.get('xml.scene') || currentScene || '');
        if (idPath && isReadyKrpano && activeScene && activeScene !== lastScene) {
            setLastScene(activeScene);

            // Stop audio player when changing scenes
            if (isAudioPlayerVisible) {
                stopAudio();
            }

            const setupPolygonHotspots = async () => {
                try {
                    await fetchMultimedia(activeScene);
                } catch {
                    // Error fetching multimedia
                }
            };
            setupPolygonHotspots();
        }
    }, [currentScene, isReadyKrpano, krpano, idPath, lastScene, fetchMultimedia, isAudioPlayerVisible, stopAudio]);

    // Disable/Enable all hotspots interaction based on form state
    // When editing (isUpdateFormOpen), keep the currently edited hotspot enabled for dragging
    useEffect(() => {
        if (!krpano || hotspotDataList.length === 0) return;

        const isBusy = isUpdateFormOpen || isCreateFormOpen;
        // Get the ID of the hotspot being edited (if any)
        const editingHotspotId = updateFormHotspot?.id?.toString();

        hotspotDataList.forEach((hotspot) => {
            const id = hotspot.id.toString();
            const names = [
                `hotspot_${id}`,
                `hotspot_${id}_multimedia`,
                `hotspot_${id}_text`,
                `hotspot_${id}_multimedia_text`
            ];

            // If this is the hotspot being edited, keep it enabled for dragging
            const isBeingEdited = editingHotspotId && id === editingHotspotId;

            names.forEach(name => {
                if (krpano.get(`hotspot[${name}]`)) {
                    // Keep hotspot enabled if it's the one being edited
                    krpano.set(`hotspot[${name}].enabled`, isBeingEdited ? true : !isBusy);
                }
            });
        });
    }, [isUpdateFormOpen, isCreateFormOpen, krpano, hotspotDataList, updateFormHotspot]);

    // Expose showMultimediaPopup to window for krpano onclick handlers
    useEffect(() => {
        if (typeof window !== 'undefined') {
            (window as any).showMultimediaPopup = (hotspotId: string, x: number, y: number, fallbackData?: any) => {
                // If any form (create or update) is open, disable clicking on other hotspots
                if (isUpdateFormOpen || isCreateFormOpen) {
                    console.log('Multimedia form is open, skipping popup');
                    return;
                }

                // Find the multimedia item by ID or entity_id or fallback
                let multimedia = hotspotDataList.find(item =>
                    item.id?.toString() === hotspotId ||
                    item.entity_id?.toString() === hotspotId
                );

                if (!multimedia && fallbackData) {
                    multimedia = fallbackData;
                }

                if (multimedia) {
                    // Check if this is audio multimedia - play audio but still show popup
                    if (multimedia.entity_type === 'audio' || multimedia.link_type === 'Âm thanh') {
                        // Parse config to get audio settings
                        let audioConfig = null;
                        try {
                            if (multimedia.config) {
                                const config = typeof multimedia.config === 'string'
                                    ? JSON.parse(multimedia.config)
                                    : multimedia.config;
                                audioConfig = config.audio;
                            }
                        } catch (e) {
                            console.warn('[MultimediaManager] Failed to parse audio config:', e);
                        }

                        // If audio config exists, play audio
                        if (audioConfig && audioConfig.enabled && audioConfig.url) {
                            playAudio({
                                url: audioConfig.url,
                                title: audioConfig.title || multimedia.title || multimedia.name_vn || 'Âm thanh',
                                volume: audioConfig.volume !== undefined ? audioConfig.volume : 1.0,
                                loop: audioConfig.loop || false,
                                autoplay: audioConfig.autoplay !== false,
                                showTimeline: audioConfig.showTimeline !== false,
                            });
                            // Continue to show popup for edit/delete functionality
                        }
                    }

                    // Parse config to get title if multimedia.title is empty
                    let parsedConfig: any = null;
                    try {
                        if (multimedia.config) {
                            parsedConfig = typeof multimedia.config === 'string'
                                ? JSON.parse(multimedia.config)
                                : multimedia.config;
                        }
                    } catch (e) {
                        console.warn('[MultimediaManager] Failed to parse config:', e);
                    }

                    const resolvedTitle = multimedia.title || multimedia.name_vn || parsedConfig?.title || 'Model 3D';

                    // Create a fake APIHotspot object for compatibility
                    const fakeHotspot: any = {
                        id: multimedia.id,
                        scene_id: multimedia.scene_id || 0,
                        ath: (multimedia.coordinate && multimedia.coordinate[0]?.ath) ?? multimedia.ath ?? 0,
                        atv: (multimedia.coordinate && multimedia.coordinate[0]?.atv) ?? multimedia.atv ?? 0,
                        link: multimedia.entity_id || 0,
                        style: multimedia.style || 'default',
                        style_hover: multimedia.style_hover || 'default',
                        is_show: multimedia.is_show || 1,
                        title: resolvedTitle,
                        scene_name: multimedia.scene_name || currentScene || '',
                        target_scene_name: multimedia.target_scene_name,

                        // Added fields for Textbox and other types
                        type: multimedia.type,
                        entity_type: multimedia.entity_type,
                        config: multimedia.config,
                        translations: multimedia.translations,
                        color_fill: multimedia.color_fill,
                        color_fill_alpha: multimedia.color_fill_alpha,
                        border_width: multimedia.border_width,
                        color_line: multimedia.color_line,
                        coordinates: multimedia.coordinate || multimedia.coordinates || []
                    };

                    showPopup(fakeHotspot, { x, y });
                }
            };

            return () => {
                delete (window as any).showMultimediaPopup;
            };
        }
    }, [hotspotDataList, currentScene, showPopup, playAudio, isUpdateFormOpen, isCreateFormOpen]);

    const reloadSingleHotspot = useCallback((hotspotId: number | string) => {
        const id = hotspotId.toString();
        const hotspot = hotspotDataList.find(h => h.id.toString() === id);

        if (hotspot && krpano) {
            // Remove existing hotspot visualization first
            try {
                const hotspotName = `hotspot_${id}`;
                const polygonName = `hotspot_${id}_multimedia`;

                // Remove main hotspots
                krpano.call(`removehotspot(${hotspotName})`);
                krpano.call(`removehotspot(${polygonName})`);

                // Remove text hotspots
                krpano.call(`removehotspot(${hotspotName}_text)`);
                krpano.call(`removehotspot(${polygonName}_text)`);

                // Also remove any associated lights for 3D models if applicable
                const lights = [`light1_${id}`, `light2_${id}`, `light3_${id}`, `light6_${id}`];
                lights.forEach(lightName => {
                    try {
                        krpano.call(`removehotspot(${lightName})`);
                    } catch (e) { }
                });
            } catch (e) {
                console.warn('[MultimediaManager] Error removing hotspot during single reload:', e);
            }

            // Re-render based on type
            const isAudio = hotspot.type === 'audio' || hotspot.entity_type === 'audio' || hotspot.link_type === 'Âm thanh';

            if (hotspot.type === 'polygon' || hotspot.type === 'hotspot_3d') {
                renderPolygonHotspots(hotspot);
            } else if (isAudio || hotspot.type === 'hotspot') {
                renderHotspot(hotspot, true); // true = skip exist check (force render)
            } else if (hotspot.type === 'textbox' || hotspot.entity_type === 'textbox') {
                renderTextbox(hotspot);
            } else if (hotspot.type === 'image2d' || hotspot.entity_type === 'image2d') {
                renderImage2D(hotspot);
            } else if (hotspot.type === 'video2d' || hotspot.entity_type === 'video2d') {
                renderVideo2D(hotspot);
            } else {
                // Default fallback
                renderHotspot(hotspot, true);
            }
        }
    }, [hotspotDataList, krpano, renderHotspot, renderPolygonHotspots, renderTextbox, renderImage2D, renderVideo2D]);

    return (
        <MultimediaManagerContext.Provider
            value={{
                hotspots,
                currentSceneHotspots,
                hotspotDataList,
                isLoading,
                fetchMultimedia,
                popupHotspot,
                popupPosition,
                isPopupVisible,
                showPopup,
                hidePopup,
                audioPlayerData,
                isAudioPlayerVisible,
                playAudio,
                stopAudio,
                updateFormHotspot,
                isUpdateFormOpen,
                openUpdateForm,
                closeUpdateForm,
                updateMultimedia,
                deleteMultimedia,
                reloadSingleHotspot,
                isCreateFormOpen,
                setIsCreateFormOpen,
            }}
        >
            {children}

            {/* Audio Player */}
            {isAudioPlayerVisible && audioPlayerData && (
                <MultimediaAudioPlayer
                    audioUrl={audioPlayerData.url}
                    audioTitle={audioPlayerData.title}
                    volume={audioPlayerData.volume}
                    loop={audioPlayerData.loop}
                    autoplay={audioPlayerData.autoplay}
                    showTimeline={audioPlayerData.showTimeline}
                    onClose={stopAudio}
                    onPlay={() => {
                        // Callback when audio starts playing (optional)
                    }}
                    onPause={() => {
                        // Callback when audio pauses (optional)
                    }}
                    onEnded={() => {
                        // Callback when audio ends (optional)
                        if (!audioPlayerData.loop) {
                            stopAudio();
                        }
                    }}
                />
            )}
        </MultimediaManagerContext.Provider>
    );
};

export const useMultimediaManager = () => {
    const context = useContext(MultimediaManagerContext);
    if (context === undefined) {
        throw new Error('useMultimediaManager must be used within a MultimediaManagerProvider');
    }
    return context;
};
